



















































































































































































































































































































































import Vue from 'vue';
import { mapGetters } from 'vuex';
import { set, take, cloneDeep } from 'lodash';
import ProductPaymentPlansList from './ProductPaymentPlansList';
import PaymentPlanDialog from '@/web/views/admin/products/product-form/PaymentPlanDialog';
import { errorToMessage } from '@/core/utils/errors';
import TeacherPermissionsMixin from '@/web/mixins/TeacherPermissionsMixin';

interface Type {
  name: string;
  translation: string;
  wishTranslation?: string;
}

interface LocalState {
  recurringTypes: Type[];
  periodTypes: Type[];
}

export default Vue.extend({
  name: 'ProductPricing',
  components: {
    ProductPaymentPlansList,
  },
  mixins: [TeacherPermissionsMixin],
  data(): LocalState {
    return {
      recurringTypes: [
        {
          name: 'annually',
          translation: 'GENERAL.RECURRING_TYPE.ANNUALLY',
          wishTranslation: 'GENERAL.MIN_RECURRING_TYPE_PAYMENT.ANNUALLY',
        },
        {
          name: 'biannually',
          translation: 'GENERAL.RECURRING_TYPE.BIANNUALLY',
          wishTranslation: 'GENERAL.MIN_RECURRING_TYPE_PAYMENT.BIANNUALLY',
        },
        {
          name: 'quarterly',
          translation: 'GENERAL.RECURRING_TYPE.QUARTERLY',
          wishTranslation: 'GENERAL.MIN_RECURRING_TYPE_PAYMENT.QUARTERLY',
        },
        {
          name: 'monthly',
          translation: 'GENERAL.RECURRING_TYPE.MONTHLY',
          wishTranslation: 'GENERAL.MIN_RECURRING_TYPE_PAYMENT.MONTHLY',
        },
        {
          name: 'weekly',
          translation: 'GENERAL.RECURRING_TYPE.WEEKLY',
          wishTranslation: 'GENERAL.MIN_RECURRING_TYPE_PAYMENT.WEEKLY',
        },
        {
          name: 'daily',
          translation: 'GENERAL.RECURRING_TYPE.DAILY',
          wishTranslation: 'GENERAL.MIN_RECURRING_TYPE_PAYMENT.DAILY',
        },
        {
          name: 'multiple',
          translation: 'GENERAL.RECURRING_TYPE.MULTIPLE',
        },
      ],
      periodTypes: [
        {
          name: 'D',
          translation: 'GENERAL.PERIOD.DAYS',
        },
        {
          name: 'W',
          translation: 'GENERAL.PERIOD.WEEKS',
        },
        {
          name: 'M',
          translation: 'GENERAL.PERIOD.MONTHS',
        },
      ],
      paymentPlans: [],
      includedProducts: [],
    };
  },
  computed: {
    ...mapGetters({
      productForm: 'product/productForm',
      teacherTaxRates: 'product/teacherTaxRates',
    }),
    recurringStrategies() {
      return [
        {
          code: 'fixed',
          label: this.$t('PRODUCTS.LABELS.RECURRING_STRATEGY_FIXED'),
        },
        {
          code: 'add',
          label: this.$t('PRODUCTS.LABELS.RECURRING_STRATEGY_ADD'),
        },
        {
          code: 'topup',
          label: this.$t('PRODUCTS.LABELS.RECURRING_STRATEGY_TOPUP'),
        },
      ];
    },
    hasRecurringStrategy() {
      return this.hasPrivateSessions;
    },
    numberOfIncludedSessions: {
      get(): number {
        return this.productForm
          ? this.productForm.number_of_included_sessions
          : 0;
      },
      set(value: number) {
        this.$store.dispatch('product/setProductForm', {
          number_of_included_sessions: value,
        });
      },
    },
    showMultiplePurchases() {
      return this.productForm.product_type !== 'digital_download';
    },
    showRecurringPayments() {
      return this.productForm.product_type !== 'digital_download';
    },
    showAddPaymentPlan() {
      return this.productForm.product_type !== 'digital_download';
    },
    hasPrivateSessions() {
      return this.productForm?.has_private_sessions;
    },
    hasPaymentPlanPermissions(): boolean {
      return this.hasPermission('has_payment_plans');
    },
    hasPayWhatYouWishPermissions(): boolean {
      return this.hasPermission('has_pay_what_you_can');
    },
    isPricingTypeFree(): boolean {
      return this.productForm.pricing_type === 'free';
    },
    isPricingTypeFixed(): boolean {
      return this.productForm.pricing_type === 'fixed';
    },
    isPricingTypeRecurring(): boolean {
      return this.productForm.pricing_type === 'recurring';
    },
    isRecurringTypeMultiple(): boolean {
      return this.productForm.recurring_type === 'multiple';
    },
    recurringOptionsList(): Type[] {
      return take(this.recurringTypes, this.recurringTypes.length - 1);
    },
    pricingType: {
      get(): string {
        if (this.productForm.pricing_type) return this.productForm.pricing_type;
        return 'free';
      },
      set(value: string) {
        this.$store.dispatch('product/setProductForm', {
          pricing_type: value,
          price: null,
          day_pass_enabled: false,
          day_pass_price: null,
          trial: false,
          trialPeriod: null,
          trialUnits: null,
          trial_period_selected: null,
        });
        if (this.isPricingTypeRecurring) {
          this.recurringType = this.productForm.recurring_type ?? 'monthly';
        } else {
          this.recurringType = null;
        }
      },
    },
    price: {
      get(): string {
        return this.productForm.price;
      },
      set(value: string) {
        this.$store.dispatch('product/setProductForm', {
          price: value,
        });
      },
    },
    checkoutSubtitle: {
      get(): string {
        return this.productForm.checkout_subtitle;
      },
      set(value: string) {
        this.$store.dispatch('product/setProductForm', {
          checkout_subtitle: value,
        });
      },
    },
    recurringType: {
      get(): string {
        return this.productForm.recurring_type ?? 'monthly';
      },
      set(value: string) {
        this.$store.dispatch('product/setProductForm', {
          recurring_type: value,
        });
      },
    },
    recurringOptions: {
      get(): RecurringOptions {
        const defaultOption = () => {
          return {
            available: false,
            price: '',
            min_wish_price: '',
            number_of_punches: '',
            number_of_included_sessions: '',
          };
        };

        return {
          annually: defaultOption(),
          biannually: defaultOption(),
          daily: defaultOption(),
          monthly: defaultOption(),
          quarterly: defaultOption(),
          weekly: defaultOption(),
          ...this.productForm.recurring_options,
        };
      },
      set(value: RecurringOptions) {
        this.$store.dispatch('product/setProductForm', {
          recurring_options: value,
        });
      },
    },
    dayPassEnabled: {
      get(): boolean {
        if (this.productForm.day_pass_enabled)
          return this.productForm.day_pass_enabled;
        return false;
      },
      set(value: boolean) {
        this.$store.dispatch('product/setProductForm', {
          day_pass_enabled: value,
        });
      },
    },
    dayPassPrice: {
      get(): string {
        return this.productForm.day_pass_price;
      },
      set(value: string) {
        this.$store.dispatch('product/setProductForm', {
          day_pass_price: value,
        });
      },
    },
    trial: {
      get(): boolean {
        if (this.productForm.trial) return this.productForm.trial;
        return false;
      },
      set(value: boolean) {
        this.$store.dispatch('product/setProductForm', {
          trial: value,
        });
        if (value) {
          this.trialPeriod = this.productForm.trial_period ?? 'D';
        } else {
          this.trialPeriod = null;
          this.trialUnits = null;
        }
      },
    },
    trialPeriod: {
      get(): string {
        return this.productForm.trial_period ?? 'D';
      },
      set(value: string | null) {
        this.$store.dispatch('product/setProductForm', {
          trial_period: value,
          trial_period_selected: {
            value: value,
          },
        });
      },
    },
    trialUnits: {
      get(): number {
        return this.productForm.trial_units;
      },
      set(value: number | null) {
        this.$store.dispatch('product/setProductForm', {
          trial_units: value,
        });
      },
    },
    multiplePurchases: {
      get(): boolean {
        return this.productForm.multiple_purchases;
      },
      set(value: boolean) {
        this.$store.dispatch('product/setProductForm', {
          multiple_purchases: value,
        });
      },
    },
    wishPriceEnabled: {
      get(): boolean {
        return this.productForm.wish_price_enabled;
      },
      set(value: boolean) {
        this.$store.dispatch('product/setProductForm', {
          wish_price_enabled: value,
        });
      },
    },
    wishPrice: {
      get(): boolean {
        return this.productForm.min_wish_price;
      },
      set(value: boolean) {
        this.$store.dispatch('product/setProductForm', {
          min_wish_price: value || null,
        });
      },
    },
    getCurrencySymbol() {
      return this.productForm.currency;
    },
    mappedTeacherTaxRates() {
      if (!this.teacherTaxRates) return [];
      return this.teacherTaxRates
        .filter(taxRate => taxRate.active)
        .map(taxRate => {
          return {
            ...taxRate,
            name: `${taxRate.name} (${taxRate.rate}%)`,
          };
        });
    },
    isBundle() {
      return this.productForm.product_type === 'bundle';
    },
    incompatiblePricedPrivateSessionProducts() {
      const differentlyPricedProducts = [];
      this.includedProducts.forEach(product => {
        // for product with private sessions, check if this.productForm.pricing_type or product.pricing_type is recurring and if they are different or do not have the same recurring_options set
        if (product.has_private_sessions) {
          if (
            this.productForm.pricing_type === 'recurring' &&
            product.pricing_type === 'recurring'
          ) {
            if (this.productForm.recurring_type !== product.recurring_type) {
              differentlyPricedProducts.push(product);
            } else if (
              this.productForm.recurring_type === 'multiple' &&
              product.recurring_type === 'multiple'
            ) {
              Object.keys(this.productForm.recurring_options).forEach(key => {
                const option = this.productForm.recurring_options[key];
                if (
                  option.available !== product.recurring_options[key].available
                ) {
                  differentlyPricedProducts.push(product);
                }
              });
            }
          }
        }
      });
      return differentlyPricedProducts;
    },
  },
  methods: {
    handleRecurrentOption(identifier: string, value: number): void {
      const recurringOptions = cloneDeep(this.recurringOptions);
      set(recurringOptions, `${identifier}.price`, value);
      set(recurringOptions, `${identifier}.available`, value > 0);
      this.recurringOptions = recurringOptions;
    },
    handleWishOption(identifier: string, value: number): void {
      const recurringOptions = cloneDeep(this.recurringOptions);
      set(recurringOptions, `${identifier}.min_wish_price`, value);
      this.recurringOptions = recurringOptions;
    },
    async addPaymentPlan() {
      this.$modal.show(PaymentPlanDialog, {
        currency: this.getCurrencySymbol,
        fixedPriceAmount: parseFloat(this.price || 0),
        productId: this.productForm.id,
        onConfirm: async paymentPlan => {
          try {
            const createdPaymentPlan = await this.$store.dispatch(
              'product/addPaymentPlan',
              { productId: this.productForm.id, paymentPlan }
            );
            this.paymentPlans.push(createdPaymentPlan);
          } catch (error) {
            this.$notify({
              group: 'app',
              type: 'error',
              title: 'Failed to add a payment plan',
              text: errorToMessage(error?.response, `Request failed: ${error}`),
              duration: -1,
            });
          }
        },
      });
    },
    async editPaymentPlan(paymentPlan) {
      this.$modal.show(PaymentPlanDialog, {
        paymentPlan: paymentPlan,
        productId: this.productForm.id,
        onConfirm: async updatedPaymentPlan => {
          try {
            await this.$store.dispatch('product/updatePaymentPlan', {
              productId: this.productForm.id,
              paymentPlan: updatedPaymentPlan,
            });
            const existingPP = this.paymentPlans.find(
              pp => pp.id === updatedPaymentPlan.id
            );
            if (existingPP) {
              this.$set(
                this.paymentPlans,
                this.paymentPlans.indexOf(existingPP),
                updatedPaymentPlan
              );
            } else {
              this.paymentPlans.push(updatedPaymentPlan);
            }
          } catch (error) {
            this.$notify({
              group: 'app',
              type: 'error',
              title: 'Failed to update payment plan',
              text: errorToMessage(error?.response, `Request failed: ${error}`),
              duration: -1,
            });
          }
        },
      });
    },
    async removePaymentPlan(paymentPlan) {
      try {
        await this.$store.dispatch('product/removePaymentPlan', {
          productId: this.productForm.id,
          paymentPlan: paymentPlan,
        });
        this.paymentPlans = this.paymentPlans.filter(
          pp => pp.id !== paymentPlan.id
        );
        this.$store.dispatch('product/setProductForm', {
          payment_plans: this.paymentPlans,
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to remove payment plan',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
  },
  async mounted() {
    this.paymentPlans = this.productForm.payment_plans;
    if (this.isBundle) {
      this.includedProducts = await this.$store.dispatch(
        'product/getIncludedProducts',
        this.productForm.id
      );
    }
  },
});
